import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { MultiUserCombobox } from '@/components/ui/MultiUserCombobox';
import { SingleUserCombobox } from '@/components/ui/SingleUserCombobox';
import { useUsersList } from '@/contexts/UsersListProvider';
import { useTheme } from '@/contexts/ThemeContext';
import { getDocument, saveDocument } from '@/services/documentsService';
import { useTenancy } from '../../../contexts/TenancyContext';
import { getAuthUID } from '@/lib/authState';
import HelpPopoverHover, { HelpPopoverHoverProps } from '@/components/HelpPopoverHover';

interface ReviewDocDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedEvent?: {
    title?: string;
    description?: string;
    date?: string;
    id?: string;

    nextReviewDate?: string;
    lastReviewDate?: string;
    fileName?: string;
  };
  onDataChanged?: () => void;
  helpData: HelpPopoverHoverProps;
}

interface Signer {
  label: string;
  value: string;
}

const mockSigners: Signer[] = [
  { label: 'Alice Smith', value: 'alice@company.com' },
  { label: 'Bob Jones', value: 'bob@company.com' },
  { label: 'External Signer', value: 'external@email.com' },
];

function ReviewedByCombobox({ reviewedBy, setReviewedBy }: { reviewedBy: string, setReviewedBy: (v: string) => void }) {
  const { users, loading } = useUsersList();
  return (
    <SingleUserCombobox
      selected={reviewedBy}
      setSelected={setReviewedBy}
      placeholder="Select reviewer..."
      className="w-full"
    />
  );
}

function isValidDate(date: any): date is Date {
  if (!date) return false;
  const d = (date instanceof Date) ? date : new Date(date);
  return d instanceof Date && !isNaN(d.valueOf());
}

function cleanTitle(title?: string) {
  return title?.replace(/^Review Document:\s*/i, '') || '';
}

const ReviewDocDialog: React.FC<ReviewDocDialogProps> = ({ open, onOpenChange, selectedEvent, onDataChanged, helpData }) => {
  // --- PATCH: Track selected file for upload (not uploaded until review complete) ---
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [replaceLoading, setReplaceLoading] = React.useState(false);
  const { isDark } = useTheme();
  const { currentUser } = useTenancy();
  const [reviewedBy, setReviewedBy] = React.useState("");
  const [signatureRequired, setSignatureRequired] = React.useState('no');
  const [signers, setSigners] = React.useState<string[]>([]);
  const [reviewStatus, setReviewStatus] = React.useState('approved');
  const [reviewNotes, setReviewNotes] = React.useState('');
  const [lastReviewDate, setLastReviewDate] = React.useState<Date | undefined>(
    new Date()
  );
  const [nextReviewDate, setNextReviewDate] = React.useState<Date | undefined>(
    new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
  );
  const [doc, setDoc] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [validationError, setValidationError] = React.useState<string | null>(null);

  // Clear validation error as soon as all required fields are filled
  React.useEffect(() => {
    if (validationError && lastReviewDate && nextReviewDate && reviewedBy) {
      setValidationError(null);
    }
  }, [lastReviewDate, nextReviewDate, reviewedBy, validationError]);

  // Set reviewedBy to current user from users list, or fallback to first user, when dialog opens or users load
  const { users, loading: usersLoading } = useUsersList();
  React.useEffect(() => {
    if (!open) return;
    let reviewerValue = '';
    if (users && users.length > 0) {
      // Try to find the current user in the users list
      const match = users.find(u => (
        u.value === currentUser?.email ||
        u.value === currentUser?.displayName
      ));
      reviewerValue = match ? match.value : users[0].value;
    } else {
      reviewerValue = currentUser?.email || currentUser?.displayName || '';
    }
    setReviewedBy(reviewerValue);
    setSignatureRequired('no');
    setSigners([]);
    setReviewStatus('approved');
    setReviewNotes('');
    setLastReviewDate(new Date());
    setNextReviewDate(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000));
    setDoc(selectedEvent?.fileName ? { fileName: selectedEvent.fileName } : null);
    setError(null);
    setLoading(false);
    setSelectedFile(null);
    setValidationError(null);
  }, [open, selectedEvent, currentUser, users, usersLoading]);

  // Load document if selectedEvent has an id
  React.useEffect(() => {
    async function loadDoc() {
      if (!selectedEvent?.id) return;
      setLoading(true);
      setError(null);
      try {
        // Use getDocument to fetch from Firestore
        const loadedDoc = await getDocument(selectedEvent.id);
        if (!loadedDoc) throw new Error('Document not found');
        // Only fill in state from backend if selectedEvent is missing a value
        if (!selectedEvent?.fileName && loadedDoc.fileName) {
          setDoc(loadedDoc);
        }
        if (loadedDoc.reviewedBy) {
          setReviewedBy(loadedDoc.reviewedBy);
        }
        if (loadedDoc.reviewStatus) {
          setReviewStatus(loadedDoc.reviewStatus);
        }
        if (loadedDoc.reviewNotes) {
          setReviewNotes(loadedDoc.reviewNotes);
        }
        if (!selectedEvent?.lastReviewDate && loadedDoc.lastReviewDate) {
          setLastReviewDate(new Date(loadedDoc.lastReviewDate));
        }
        if (!selectedEvent?.nextReviewDate && loadedDoc.nextReviewDate) {
          setNextReviewDate(new Date(loadedDoc.nextReviewDate));
        }
        // You may want to add similar logic for prevReviewDate or other fields as needed
      } catch (err: any) {
        setError(err.message || 'Error loading document');
        setDoc(null);
      } finally {
        setLoading(false);
      }
    }
    loadDoc();
  }, [open, selectedEvent]);

  // Download handler using SupportingDocs.Document
  const handleDownload = async () => {
    if (!doc) return;
    try {
      await doc.download();
    } catch (e: any) {
      setError(e.message || 'Download failed');
    }
  };

  // Replace handler using documentsService (if file replace is supported)
  const handleReplace = async (file: File) => {
    if (!doc) return;
    try {
      // Save updated file using documentsService
      await saveDocument(doc, file);
      // Refetch the document after replace
      const updatedDoc = await getDocument(doc.docID);
      setDoc(updatedDoc);
      if (typeof onDataChanged === 'function') onDataChanged();
    } catch (e: any) {
      setError(e.message || 'Replace failed');
    }
  };

  // Utility to remove undefined fields
  function removeUndefined(obj: any) {
    return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== undefined));
  }

  // Complete Review handler
  const handleCompleteReview = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[ReviewDocDialog] Submit handler called', { lastReviewDate, nextReviewDate, reviewedBy });
    setValidationError(null);
    // Validation: required fields
    if (!lastReviewDate) {
      setValidationError('Review date is required.');
      return;
    }
    if (!nextReviewDate) {
      setValidationError('Next review date is required.');
      return;
    }
    if (!reviewedBy) {
      setValidationError('Reviewed by is required.');
      return;
    }
    if (!doc) return;
    setLoading(true);
    setError(null);
    try {
      // Gather all relevant fields
      const {
        classification,
        procRef,
        confluenceURL,
        fileName,
        docHash,
      } = doc;
      // Prepare review data
      const reviewData: any = {
        docID: doc.docID,
        companyID: window.companyID,
        comments: reviewNotes,
        lastReviewDate: lastReviewDate,
        nextReviewDate: nextReviewDate || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
        reviewedBy,
        reviewStatus,
        signatureRequired,
        signers,
        classification,
        procRefs: procRef,
        uploadedBy: currentUser?.userID || getAuthUID(),
        confluenceURL,
        fileName,
        docHash,
        edit: true,
        // Add any other fields as needed
      };

      // Remove undefined fields before sending
      const cleanedReviewData = removeUndefined(reviewData);

      // Update doc instance fields
      Object.assign(doc, cleanedReviewData);

      // PATCH: Upload file if one is selected
      await saveDocument(doc, selectedFile);
      setSelectedFile(null);
      if (typeof onDataChanged === 'function') onDataChanged();
      onOpenChange(false);
    } catch (err: any) {
      setError(err.message || 'Error completing review');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        className={`max-w-3xl! w-full max-h-[90vh] overflow-y-auto rounded-xl shadow-lg border border-white/10 p-0!`} aria-describedby={undefined}>
        <div className="flex flex-col gap-4 h-full bg-card overflow-hidden">
          <div className="sticky top-0 z-20 py-6 border-b px-4 border-white/10 bg-gradient-to-b from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60">
            <DialogTitle className="text-foreground">Document Review</DialogTitle>
            <DialogDescription
              className="text-muted-foreground truncate w-full max-w-[550px]"
              title={selectedEvent?.title || selectedEvent?.fileName || doc?.fileName}
            >
              {selectedEvent?.title || selectedEvent?.fileName || doc?.fileName || 'Review document and update its status'}
            </DialogDescription>
          </div>

          {validationError && (
            <div className="flex items-center px-4 text-red-600 text-sm mb-2 font-semibold">
              <svg className="h-5 w-5 mr-1 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" /></svg>
              {validationError}
            </div>
          )}
          <form onSubmit={handleCompleteReview}>
            <div className="grid gap-4 pb-4 px-4">
              {/* Name */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="doc-name" className="text-right text-foreground">
                  Name
                </Label>
                <Input
                  id="doc-name"
                  value={
                    selectedEvent?.fileName
                      ? selectedEvent.fileName
                      : cleanTitle(selectedEvent?.title) || ''
                  }
                  className="col-span-3"
                  readOnly
                  autoFocus={false}
                />
              </div>

              {/* Reviewed By */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="reviewed-by" className="text-right text-foreground">
                  Reviewed By
                </Label>
                <div className="col-span-3 w-full">
                  <div className="w-full">
                    <ReviewedByCombobox reviewedBy={reviewedBy} setReviewedBy={setReviewedBy} />
                  </div>
                </div>
              </div>

              {/* Signature Required */}
              {/* <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="signature-required" className="text-right text-foreground whitespace-nowrap">
                Signature Required
              </Label>
              <div className="col-span-3 w-full">
                <Select value={signatureRequired} onValueChange={setSignatureRequired}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div> */}

              {/* Signers (if required) */}
              {/* {signatureRequired === 'yes' && (
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="signers" className="text-right text-foreground">
                  Signers
                </Label>
                <div className="col-span-3 w-full">
                  <div className="w-full">
                    <MultiUserCombobox
                      users={mockSigners}
                      selected={signers}
                      setSelected={setSigners}
                      placeholder="Select signers..."
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            )} */}

              {/* Review Date */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="doc-review-date" className="text-right text-foreground">
                  Date Reviewed
                </Label>
                <div className="col-span-3">
                  <DatePicker
                    date={isValidDate(lastReviewDate) ? lastReviewDate : undefined}
                    setDate={setLastReviewDate}
                    fullWidth
                    enableNaturalInput showPreview placeholder="Type 'tomorrow' or pick a date"
                  />
                </div>
              </div>

              {/* Next Review Date */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="doc-next-review-date" className="text-right text-foreground">
                  Next Review Date
                </Label>
                <div className="col-span-3">
                  <DatePicker
                    date={isValidDate(nextReviewDate) ? nextReviewDate : undefined}
                    setDate={setNextReviewDate}
                    fullWidth
                    enableNaturalInput showPreview placeholder="Type 'tomorrow' or pick a date"
                  />
                </div>
              </div>

              {/* Review Notes */}
              <div className="grid grid-cols-4 items-start gap-4">
                <Label htmlFor="review-notes" className="text-right text-foreground">
                  Comments
                </Label>
                <Textarea
                  id="review-notes"
                  placeholder="Enter comments about this document review..."
                  className="col-span-3"
                  rows={4}
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                />
              </div>

              {/* Download/Replace Buttons */}
              <div className="grid grid-cols-4 items-center gap-4">
                <div className="col-span-1"></div>
                <Button type="button" variant="outline" onClick={handleDownload} className="col-span-1 border border-gray-200 hover:bg-gray-50 hover:text-gray-700">
                  Download
                </Button>
                {/* Replace Button: only selects file, does not upload */}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className={`col-span-1 border border-gray-200 hover:bg-gray-50 hover:text-gray-700 ${selectedFile ? 'bg-green-100 border-green-400' : ''}`}
                  disabled={!!selectedFile}
                >
                  {selectedFile ? (
                    <span className="flex items-center text-green-700">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 text-green-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      Selected
                    </span>
                  ) : 'Replace'}
                </Button>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setSelectedFile(file);
                    setReplaceLoading(false);
                    setError(null);
                    e.target.value = '';
                  }}
                />
                {/* Show selected file name and clear option */}
                <div className="col-span-1 flex flex-col items-start">
                  {selectedFile && (
                    <span className="text-xs text-green-700 flex items-center">
                      {selectedFile.name}
                      <Button size="icon" variant="ghost" className="ml-1 p-1" onClick={() => setSelectedFile(null)}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </Button>
                    </span>
                  )}
                </div>
                <div className="col-span-1"></div>
              </div>
            </div>
          </form>

          <DialogFooter className="mt-4 p-4 sticky bottom-0 z-20 border-white/10 bg-gradient-to-t from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60 border-t flex! flex-row! justify-between! items-center! align-center! gap-2">
            <div className="text-xs text-gray-400 mt-2 ml-1">Component ID: {selectedEvent?.id || ''}</div>
            <div className="flex flex-row gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border border-gray-200 hover:bg-gray-50 hover:text-black dark:hover:text-white" disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Complete Review'}
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewDocDialog;
